import React, { useState, useEffect } from 'react';
import { Modal, Button, ProgressBar, ButtonGroup, Card, Collapse } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import TaskList from './task-modal/TaskList';
import TaskDetails from './task-modal/TaskDetails';
import NewTaskModal from '../../modals/NewTaskModal';
import EmailOverlayModal from './EmailOverlayModal';
import TaskOverlayModal from './TaskOverlayModal';
import { Task } from './Interfaces';
import { useEmailPanel } from '../../pages/mail/contexts/EmailPanelContext';
import { FeedEmailData } from '../../pages/mail/components/EmailFeedContent';
import { formatDistanceToNow } from 'date-fns';

interface Job {
  id: string;
  jobLabel: string;
  description: string;
  customerId: string[];
  // Legacy timer fields — optional for backward compat with the shared Job type.
  unbilledHours?: number;
  totalHours?: number;
  creationDate: string;
  dueDate: string;
  status: string;
  lastEdited: string;
}

// Use the FeedEmailData from EmailPanelContext instead of local Email interface
interface Email {
  id: string;
  subject: string;
  date: Date;
  snippet: string;
  customerId?: string | null;
  text: string;
  html?: string | null;
  from: {
    address: string;
    text: string;
    name?: string;
  }[];
  formattedDate?: string;
  attachments?: any[];
}

interface Props {
  className?: string;
  job: Job;
  show: boolean;
  onHide: () => void;
  initialTaskId?: string;
}

const TaskModal: React.FC<Props> = ({ job, show, onHide, initialTaskId }): JSX.Element => {
  // Use EmailPanelContext for email data
  const { preLoadedEmails, isLoadingInitialEmails, loadEmailsForDateRange } = useEmailPanel();
  
  // State for tasks
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  
  // State for emails  
  const [selectedEmail, setSelectedEmail] = useState<FeedEmailData | null>(null);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isLoadingDateRangeEmails, setIsLoadingDateRangeEmails] = useState(false);
  
  // State for overlays
  const [showEmailOverlay, setShowEmailOverlay] = useState(false);
  const [showTaskOverlay, setShowTaskOverlay] = useState(false);
  
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  const [emailDateRange, setEmailDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });

  // Fetch tasks
  useEffect(() => {
    if (job && show) {
      fetchTasks();
    }
  }, [job, show]);

  // When tasks are loaded and an initialTaskId is provided, open that task overlay
  useEffect(() => {
    if (!show || !initialTaskId || tasks.length === 0) return;
    const found = tasks.find(t => t.id === initialTaskId);
    if (found) {
      setSelectedTask(found);
      setShowTaskOverlay(true);
    }
  }, [show, initialTaskId, tasks]);

  // Load emails for date range when date filters change
  useEffect(() => {
    const loadDateRangeEmails = async () => {
      if (emailDateRange.start || emailDateRange.end) {
        if (!job.customerId || job.customerId.length === 0) return;
        
        setIsLoadingDateRangeEmails(true);
        
        try {
          // Set default dates if not provided
          const startDate = emailDateRange.start ? new Date(emailDateRange.start) : new Date('2020-01-01');
          const endDate = emailDateRange.end ? new Date(emailDateRange.end) : new Date();
          
          // Ensure end date includes the full day
          endDate.setHours(23, 59, 59, 999);
          
          const primaryCustomerId = job.customerId[0];
          await loadEmailsForDateRange(startDate, endDate, primaryCustomerId);
          
        } catch (error) {
          console.error('Error loading emails for date range:', error);
        } finally {
          setIsLoadingDateRangeEmails(false);
        }
      }
    };

    // Debounce the date range loading to avoid excessive requests
    const timeoutId = setTimeout(loadDateRangeEmails, 500);
    return () => clearTimeout(timeoutId);
  }, [emailDateRange.start, emailDateRange.end, job.customerId, loadEmailsForDateRange]);

  const fetchTasks = async () => {
    if (!job) return;

    setLoadingTasks(true);
    try {
      const tasksQuery = query(
        collection(db, 'tasks'),
        where('jobId', '==', job.id),
        orderBy('lastEdited', 'desc')
      );
      
      const tasksSnapshot = await getDocs(tasksQuery);
      const tasksList = tasksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Task[];
      
      setTasks(tasksList);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleOpenNewTaskModal = () => {
    setIsNewTaskModalOpen(true);
  };

  const handleCloseNewTaskModal = () => {
    setIsNewTaskModalOpen(false);
  };

  const handleNewTaskAdded = (newTask: Task) => {
    fetchTasks(); // Refresh tasks
    setSelectedTask(newTask);
  };

  const handleDeleteTask = async (task: Task) => {
    try {
      const taskRef = doc(db, 'tasks', task.id);
      await deleteDoc(taskRef);
      setTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
      if (selectedTask?.id === task.id) {
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  // Filter emails for this job's customer
  const getJobEmails = () => {
    if (!job.customerId || job.customerId.length === 0) return [];
    
    const primaryCustomerId = job.customerId[0];
    return preLoadedEmails.filter(email => email.customerId === primaryCustomerId);
  };

  // Filter emails based on search term and date range
  const getFilteredEmails = () => {
    const jobEmails = getJobEmails();
    
    let filtered = jobEmails;
    
    // Filter by search term
    if (emailSearchTerm) {
      const searchLower = emailSearchTerm.toLowerCase();
      filtered = filtered.filter(email => 
        (email.subject || '').toLowerCase().includes(searchLower) ||
        (email.text || '').toLowerCase().includes(searchLower) ||
        (email.sender || '').toLowerCase().includes(searchLower) ||
        (email.from && email.from.some(sender => 
          (sender.name || '').toLowerCase().includes(searchLower) ||
          (sender.address || '').toLowerCase().includes(searchLower)
        ))
      );
    }
    
    // Filter by date range
    if (emailDateRange.start || emailDateRange.end) {
      filtered = filtered.filter(email => {
        const emailDate = email.createdAt;
        
        if (emailDateRange.start) {
          const startDate = new Date(emailDateRange.start);
          startDate.setHours(0, 0, 0, 0);
          if (emailDate < startDate) return false;
        }
        
        if (emailDateRange.end) {
          const endDate = new Date(emailDateRange.end);
          endDate.setHours(23, 59, 59, 999);
          if (emailDate > endDate) return false;
        }
        
        return true;
      });
    }
    
    return filtered;
  };

  const handleEmailClick = (email: FeedEmailData) => {
    // Email already has complete data from EmailPanelContext, no need to fetch again
    setSelectedEmail(email);
    setShowEmailOverlay(true);
    setIsEmailLoading(false); // No loading needed since data is already available
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setShowTaskOverlay(true);
  };

  const handleTaskUpdated = (updatedTask: Task) => {
    setTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t));
    setSelectedTask(updatedTask);
  };

  const filteredTasks = tasks.filter(task => 
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCloseModal = () => {
    setSelectedEmail(null);
    setSelectedTask(null);
    setShowEmailOverlay(false);
    setShowTaskOverlay(false);
    setSearchTerm('');
    setEmailSearchTerm('');
    setEmailDateRange({ start: '', end: '' });
    setIsEmailLoading(false);
    setIsLoadingDateRangeEmails(false);
    onHide();
  };

  if (!job) return <></>;

  return (
    <>
      <Modal
        show={show}
        onHide={handleCloseModal}
        fullscreen
        className="task-modal fade"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {job.jobLabel}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          <div className="job-detail-container">
            {/* Left Panel - Emails (50%) */}
            <div className="email-panel" style={{ position: 'relative' }}>
              <div className="panel-header">
                <div className="d-flex align-items-center mb-4">
                  <h5 className="text-gray-900 fw-bold mb-0">Customer Emails</h5>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {/* Search Input - flexible width to take remaining space */}
                  <div className="position-relative flex-grow-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search emails..."
                      value={emailSearchTerm}
                      onChange={(e) => setEmailSearchTerm(e.target.value)}
                      style={{ paddingLeft: '2.5rem' }}
                    />
                    <i className="bi bi-search text-muted position-absolute top-50 translate-middle-y ms-3"></i>
                  </div>
                  
                  {/* Date Range Picker - fixed width, pushed to right */}
                  <div className="d-flex align-items-center flex-shrink-0">
                    <div className="dropdown">
                      <button
                        type="button"
                        className={`btn btn-sm ${emailDateRange.start || emailDateRange.end ? 'btn-primary' : 'btn-light'}`}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        title="Filter by date range"
                      >
                        <i className="bi bi-calendar3 me-1"></i>
                        {emailDateRange.start || emailDateRange.end ? 'Date Filter Active' : 'Date Range'}
                      </button>
                      <div className="dropdown-menu p-3" style={{ minWidth: '300px' }}>
                        <div className="mb-3">
                          <label className="form-label fs-7 fw-bold text-gray-600">From Date</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={emailDateRange.start}
                            onChange={(e) => setEmailDateRange(prev => ({ ...prev, start: e.target.value }))}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label fs-7 fw-bold text-gray-600">To Date</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={emailDateRange.end}
                            onChange={(e) => setEmailDateRange(prev => ({ ...prev, end: e.target.value }))}
                          />
                        </div>
                        <div className="d-flex justify-content-between">
                          <button
                            type="button"
                            className="btn btn-sm btn-light-danger"
                            onClick={() => setEmailDateRange({ start: '', end: '' })}
                          >
                            <i className="bi bi-x me-1"></i>
                            Clear
                          </button>
                          <div className="text-muted fs-8">
                            {emailDateRange.start && emailDateRange.end && (
                              `${new Date(emailDateRange.start).toLocaleDateString()} - ${new Date(emailDateRange.end).toLocaleDateString()}`
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="panel-body">
                {/* Email List using EmailPanelContext data */}
                {(isLoadingInitialEmails || isLoadingDateRangeEmails) ? (
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex align-items-center justify-content-center flex-grow-1">
                      <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary me-3" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="text-muted fw-semibold">
                          {isLoadingDateRangeEmails ? 'Loading emails for date range...' : 'Loading emails...'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : getFilteredEmails().length === 0 ? (
                  <div className="d-flex flex-column h-100">
                    <div className="d-flex align-items-center justify-content-center flex-grow-1">
                      <div className="text-center">
                        <i className="bi bi-inbox text-muted fs-2x mb-3"></i>
                        <p className="text-muted fw-semibold mb-0">
                          {emailSearchTerm || emailDateRange.start || emailDateRange.end ? 
                            'No emails match your filters' : 
                            'No emails found for this customer'
                          }
                        </p>
                        {(emailSearchTerm || emailDateRange.start || emailDateRange.end) && (
                          <div className="mt-3">
                            {emailSearchTerm && (
                              <button 
                                className="btn btn-sm btn-light-primary me-2"
                                onClick={() => setEmailSearchTerm('')}
                              >
                                <i className="bi bi-x me-1"></i>
                                Clear Search
                              </button>
                            )}
                            {(emailDateRange.start || emailDateRange.end) && (
                              <button 
                                className="btn btn-sm btn-light-primary"
                                onClick={() => setEmailDateRange({ start: '', end: '' })}
                              >
                                <i className="bi bi-calendar-x me-1"></i>
                                Clear Dates
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="email-list-container">
                    {getFilteredEmails().map((email) => (
                      <div 
                        key={email.id} 
                        className="card border mb-3 email-card"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleEmailClick(email)}
                      >
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title text-gray-900 fw-bold mb-1 text-truncate" style={{ maxWidth: '70%' }}>
                              {email.subject || 'No Subject'}
                            </h6>
                            <small className="text-muted">
                              {formatDistanceToNow(email.createdAt, { addSuffix: true })}
                            </small>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <span className="badge badge-light-primary me-2">
                              {email.from && email.from.length > 0 ? 
                                (email.from[0].name || email.from[0].address) : 
                                'Unknown Sender'
                              }
                            </span>
                            {email.attachments && email.attachments.length > 0 && (
                              <span className="badge badge-light-primary">
                                <i className="bi bi-paperclip me-1"></i>
                                {email.attachments.length}
                              </span>
                            )}
                          </div>
                          <p className="card-text text-muted mb-0 fs-7" style={{ 
                            display: '-webkit-box', 
                            WebkitLineClamp: 2, 
                            WebkitBoxOrient: 'vertical', 
                            overflow: 'hidden' 
                          }}>
                            {email.text || email.message || 'No preview available'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Email Overlay - positioned within the email panel */}
              {showEmailOverlay && (
                <EmailOverlayModal
                  email={selectedEmail}
                  show={showEmailOverlay}
                  onHide={() => {
                    setShowEmailOverlay(false);
                    setSelectedEmail(null);
                  }}
                  isLoading={isEmailLoading}
                />
              )}
            </div>

            {/* Right Panel - Tasks (50%) */}
            <div className="task-panel">
              <div className="panel-header">
                <div className="d-flex align-items-center mb-4">
                  <h5 className="text-gray-900 fw-bold mb-0">
                    Tasks ({filteredTasks.length})
                  </h5>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {/* Search Input - flexible width to take remaining space */}
                  <div className="position-relative flex-grow-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search tasks..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ paddingLeft: '2.5rem' }}
                    />
                    <i className="bi bi-search text-muted position-absolute top-50 translate-middle-y ms-3"></i>
                  </div>
                  
                  {/* New Task Button - fixed width, pushed to right */}
                  <div className="d-flex align-items-center flex-shrink-0">
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={handleOpenNewTaskModal}
                    >
                      <i className="bi bi-plus me-1"></i>
                      New Task
                    </button>
                  </div>
                </div>
              </div>
              <div className="panel-body">
                {loadingTasks ? (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="d-flex align-items-center">
                      <div className="spinner-border spinner-border-sm text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted fw-semibold">Loading tasks...</span>
                    </div>
                  </div>
                ) : (
              <TaskList 
                tasks={filteredTasks} 
                handleDeleteTask={handleDeleteTask} 
                setSelectedTask={setSelectedTask} 
                searchTerm={searchTerm}
                currentJob={job}
                onTaskUpdated={handleTaskUpdated}
                    onTaskClick={handleTaskClick}
                  />
                )}
              </div>
              
              {/* Task Overlay - positioned within the task panel */}
              {showTaskOverlay && (
                <TaskOverlayModal
                  task={selectedTask}
                  show={showTaskOverlay}
                  onHide={() => {
                    setShowTaskOverlay(false);
                    setSelectedTask(null);
                  }}
                  onTaskUpdated={handleTaskUpdated}
                />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>



      <NewTaskModal 
        isOpen={isNewTaskModalOpen} 
        onClose={handleCloseNewTaskModal} 
        currentJob={job} 
        onNewTaskAdded={handleNewTaskAdded} 
      />



      <style>
        {`
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOutRight {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }
          .alert {
            border: none;
            border-radius: 0.5rem;
          }
          .btn-ghost {
            background: none;
            border: none;
            color: inherit;
            padding: 0.25rem 0.5rem;
          }
          .btn-ghost:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }
          .task-modal .modal-content {
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          
          .task-modal .modal-body {
            flex: 1;
            padding: 0;
            overflow: hidden;
          }
          
          .job-detail-container {
            display: flex;
            height: 100%;
            background-color: var(--bs-gray-100);
            padding: 1rem;
            gap: 1rem;
          }
          
          .email-panel,
          .task-panel {
            flex: 1;
            display: flex;
            flex-direction: column;
            background-color: var(--bs-body-bg);
            border-radius: var(--bs-border-radius);
            box-shadow: var(--bs-box-shadow-sm);
            overflow: hidden;
            transition: all 0.3s ease;
            position: relative;
          }
          
          .panel-header {
            padding: 1.5rem;
            background-color: var(--bs-gray-100);
            border-bottom: 1px solid var(--bs-border-color);
          }
          
          .panel-body {
            flex: 1;
            padding: 1rem;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
          
          .email-list-container {
            flex: 1;
            overflow-y: auto;
            padding-right: 0.5rem;
          }
          
          /* Custom scrollbar styling for email list */
          .email-list-container::-webkit-scrollbar {
            width: 6px;
          }
          
          .email-list-container::-webkit-scrollbar-track {
            background: var(--bs-gray-200);
            border-radius: var(--bs-border-radius-pill);
          }
          
          .email-list-container::-webkit-scrollbar-thumb {
            background: var(--bs-gray-400);
            border-radius: var(--bs-border-radius-pill);
          }
          
          .email-list-container::-webkit-scrollbar-thumb:hover {
            background: var(--bs-gray-500);
          }
          
          /* Responsive Design */
          @media (max-width: 768px) {
            .job-detail-container {
              flex-direction: column;
              padding: 0.5rem;
              gap: 0.5rem;
            }
          }
          
          /* Theme variables handle dark mode automatically */
          
          /* Smooth transitions */
          .email-panel,
          .task-panel,
          .panel-header,
          .panel-body {
            transition: all 0.3s ease;
          }
          
          /* Search input styling */
          .panel-header .form-control-sm {
            padding-left: 2.5rem;
          }
          
          .panel-header .position-absolute {
            pointer-events: none;
            left: 0.75rem;
          }
        `}
      </style>
    </>
  );
};

export default TaskModal;